import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LayoutGrid, LogIn } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      toast.success('Login successful!');
      navigate('/configurator');
    } else {
      toast.error(result.error || 'Login failed');
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1659720879338-160d469a3b17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3VzZSUyMHdpbmRvdyUyMGludGVyaW9yfGVufDB8fHx8MTc3NjQ0MTM2M3ww&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/40"></div>
        <div className="relative z-10 p-12 flex flex-col justify-end text-white">
          <h1 className="text-5xl font-medium tracking-tight mb-4">Premium Window Solutions</h1>
          <p className="text-xl text-slate-100">Design, quote, and order custom windows in minutes</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <LayoutGrid size={40} className="text-[#0052FF]" />
              <h2 className="text-3xl font-medium tracking-tight text-slate-900">Hi Tech Industries</h2>
            </div>
            <div>
              <h3 className="text-2xl font-medium text-slate-900">Sign in to your account</h3>
              <p className="text-sm text-slate-500 mt-2">Welcome back! Please enter your details.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="hi.techindustrysgr@gmail.com"
                className="h-11 rounded-sm border-slate-200 focus:ring-2 focus:ring-[#0052FF]/20"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="h-11 rounded-sm border-slate-200 focus:ring-2 focus:ring-[#0052FF]/20"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0052FF] hover:bg-[#0043D1] text-white rounded-sm transition-all duration-150"
              data-testid="login-submit-button"
            >
              {loading ? (
                'Signing in...'
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <LogIn size={20} />
                  Sign In
                </span>
              )}
            </Button>
          </form>

          <div className="text-center">
            <p className="text-sm text-slate-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#0052FF] hover:text-[#0043D1] font-medium" data-testid="register-link">
                Create account
              </Link>
            </p>
          </div>

          <div className="pt-6 border-t border-slate-200">
            <p className="text-xs text-slate-500 text-center">
              Demo: hi.techindustrysgr@gmail.com / admin123
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
